import React, { useState } from 'react';
import { Input } from '../Input';
import { GroupedInput } from './GroupedInput';
import { Select } from '../Select';
import { DurationInput } from '../DurationInput';
import { FrequencyInput } from './FrequencyInput';
import { TextArea } from '../TextArea';
import { CURRENT_PLAN_FIELD_INFO } from '../../data/fieldInfo';
import { type CurrentPlan, type RegimenType, type TimingOption, type DosageUnit, REGIMEN_TYPES, DOSAGE_UNITS, TIMING_OPTIONS, NEVER_CONST_REP } from '../../types/data';

interface CurrentPlanFormProps {
  onSubmit: (plan: CurrentPlan) => void;
  initialData?: CurrentPlan;
  onCancel?: () => void;
}

const defaultCurrentPlan: CurrentPlan = {
  name: '',
  type: 'peptide',
  timing: 'AM',
  frequency_interval: '1 day',
  duration_period: '8 weeks',
  duration_repeat: '',
  dosage_units: 'mg',
  dosage: undefined,
  days_on: undefined,
  days_off: undefined,
  start_date: '',
  enabled: false,
  notes: ''
};

export const CurrentPlanForm: React.FC<CurrentPlanFormProps> = ({
  onSubmit,
  initialData,
  onCancel
}) => {
  const [formData, setFormData] = useState<CurrentPlan>(initialData || defaultCurrentPlan);
  const [errors, setErrors] = useState<Partial<Record<keyof CurrentPlan, string>>>({});
  const [neverRepeat, setNeverRepeat] = useState(initialData?.duration_repeat === NEVER_CONST_REP);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CurrentPlan, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.frequency_interval.trim()) {
      newErrors.frequency_interval = 'Frequency interval is required';
    }
    if (!formData.duration_period.trim()) {
      newErrors.duration_period = 'Duration period is required';
    }
    if (!formData.start_date.trim()) {
      newErrors.start_date = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit({
        ...formData,
        duration_repeat: neverRepeat ? NEVER_CONST_REP : formData.duration_repeat
      });
    }
  };

  const handleChange = <K extends keyof CurrentPlan>(
    key: K,
    value: CurrentPlan[K]
  ) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg shadow-md">
      {/* Start Date and Enabled */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Enabled Field */}
        <div>
          <label htmlFor="enabled" className="inline-flex items-center">
            <input
              id="enabled"
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-600">Enabled</span>
          </label>
        </div>

        {/* Start Date Field */}
        <div>
          <Input
            label="Start Date"
            value={formData.start_date || ''}
            onChange={(value) => handleChange('start_date', value)}
            type="date"
            required={CURRENT_PLAN_FIELD_INFO.start_date.required}
            tooltip={CURRENT_PLAN_FIELD_INFO.start_date.description}
          />
        </div>
      </div>

      {/* Horizontal Divider */}
      <hr className="border-gray-300" />

      {/* Main Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <GroupedInput
          label="Name"
          value={formData.name}
          units={formData.type}
          options={REGIMEN_TYPES}
          onValueChange={(value) => handleChange('name', value.toString())}
          onUnitsChange={(value) => handleChange('type', value as RegimenType)}
          error={errors.name}
          required={CURRENT_PLAN_FIELD_INFO.name.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.name.description}
        />

        <GroupedInput
          label="Dosage"
          value={formData.dosage || ''}
          units={formData.dosage_units || 'mg'}
          options={DOSAGE_UNITS}
          onValueChange={(value) => handleChange('dosage', Number(value))}
          onUnitsChange={(value) => handleChange('dosage_units', value as DosageUnit)}
          type="number"
          min={0}
          required={CURRENT_PLAN_FIELD_INFO.dosage.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.dosage.description}
        />

        <Select
          label="Timing"
          value={formData.timing}
          onChange={(value) => handleChange('timing', value as TimingOption)}
          options={TIMING_OPTIONS}
          required={CURRENT_PLAN_FIELD_INFO.timing.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.timing.description}
        />

        <FrequencyInput
          label="Frequency Interval"
          value={formData.frequency_interval}
          onChange={(value) => handleChange('frequency_interval', value)}
          error={errors.frequency_interval}
          required={CURRENT_PLAN_FIELD_INFO.frequency_interval.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.frequency_interval.description}
        />

        <Input
          label="Days On"
          value={formData.days_on || ''}
          onChange={(value) => handleChange('days_on', Number(value))}
          type="number"
          min={0}
          required={CURRENT_PLAN_FIELD_INFO.days_on.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.days_on.description}
        />

        <Input
          label="Days Off"
          value={formData.days_off || ''}
          onChange={(value) => handleChange('days_off', Number(value))}
          type="number"
          min={0}
          required={CURRENT_PLAN_FIELD_INFO.days_off.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.days_off.description}
        />

        <DurationInput
          label="Duration Period"
          value={formData.duration_period}
          onChange={(value) => handleChange('duration_period', value)}
          error={errors.duration_period}
          required={CURRENT_PLAN_FIELD_INFO.duration_period.required}
          tooltip={CURRENT_PLAN_FIELD_INFO.duration_period.description}
        />

        <div className="space-y-2">
          <DurationInput
            label="Duration Repeat"
            value={formData.duration_repeat || ''}
            onChange={(value) => handleChange('duration_repeat', value)}
            disabled={neverRepeat}
            required={CURRENT_PLAN_FIELD_INFO.duration_repeat.required}
            tooltip={CURRENT_PLAN_FIELD_INFO.duration_repeat.description}
          />
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="neverRepeat"
              checked={neverRepeat}
              onChange={(e) => setNeverRepeat(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="neverRepeat" className="text-sm text-gray-600">
              Never repeat
            </label>
          </div>
        </div>
      </div>

      {/* Notes Section */}
      <div>
        <TextArea
          label="Notes"
          value={formData.notes || ''}
          onChange={(value) => handleChange('notes', value)}
          placeholder="Enter any additional notes or instructions..."
          tooltip={CURRENT_PLAN_FIELD_INFO.notes.description}
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        >
          {initialData ? 'Update' : 'Create'} Current Plan
        </button>
      </div>
    </form>
  );
};
